import { useCallback, useState } from "react";

import { invalidateCorridors } from "@/shared/hooks/useAvailableCorridors";
import { useTwoFactorAction, type UseTwoFactorActionResult } from "@/shared/hooks/useTwoFactorAction";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { isTwoFactorCodeError } from "@/shared/utils/twoFactorErrors";

import { PaymentRailsService } from "../services/paymentRails.service";
import type { ClearRailSettingScope, UpsertRailSettingPayload } from "../types/paymentRails.types";

export type RailWriteResult = { ok: true } | { ok: false; message: string };

/** `null` when the 2FA challenge was closed before a code was accepted: nothing was written. */
export type RailWriteOutcome = RailWriteResult | null;

export interface UseRailMutationsResult {
  /** True while a write is in flight — the owning dialog must block closing and double-submits. */
  isSubmitting: boolean;
  /** Normalized user-facing message of the last failed mutation; cleared on every new attempt. */
  error: string | null;
  clearError: () => void;
  /** `PUT /internal/payments/rails/:rail`, behind the 2FA challenge. */
  setRailState: (rail: string, payload: UpsertRailSettingPayload) => Promise<RailWriteOutcome>;
  /**
   * `DELETE /internal/payments/rails/:rail`, behind the 2FA challenge. A 404 counts as done — the row was
   * already gone.
   */
  clearRailScope: (rail: string, scope: ClearRailSettingScope) => Promise<RailWriteOutcome>;
  /** The challenge both writes go through; render it with `TwoFactorActionModals` next to the action. */
  twoFactor: UseTwoFactorActionResult<RailWriteResult>;
}

/**
 * The rails write path. Owns the 2FA challenge, the HTTP calls, the in-flight flag and the error
 * normalization so the dialogs stay presentational. Every successful write also drops the shared corridors
 * memo, so payins, wallets and the dispersion wizard reflect the flip without a reload.
 */
export function useRailMutations(): UseRailMutationsResult {
  const { t } = useTranslations("paymentRails");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const twoFactor = useTwoFactorAction<RailWriteResult>();
  const { start } = twoFactor;

  const normalize = useCallback(
    (caught: unknown): string =>
      HttpApiService.isAccessDeniedError(caught)
        ? t("error_no_write_permission")
        : HttpApiService.getErrorMessage(caught) || t("error_save"),
    [t],
  );

  const run = useCallback(
    async (write: () => Promise<void>, notFoundIsDone = false): Promise<RailWriteResult> => {
      setIsSubmitting(true);
      setError(null);
      try {
        await write();
        invalidateCorridors();
        return { ok: true };
      } catch (caught: unknown) {
        // A refused code goes back to the challenge, which stays open for another try.
        if (isTwoFactorCodeError(caught)) throw caught;
        if (notFoundIsDone && HttpApiService.isNotFoundError(caught)) {
          invalidateCorridors();
          return { ok: true };
        }
        const message = normalize(caught);
        setError(message);
        return { ok: false, message };
      } finally {
        setIsSubmitting(false);
      }
    },
    [normalize],
  );

  const setRailState = useCallback(
    (rail: string, payload: UpsertRailSettingPayload) =>
      start((code) => run(() => PaymentRailsService.upsert(rail, payload, code))),
    [start, run],
  );

  const clearRailScope = useCallback(
    (rail: string, scope: ClearRailSettingScope) =>
      start((code) => run(() => PaymentRailsService.clear(rail, scope, code), true)),
    [start, run],
  );

  return {
    isSubmitting,
    error,
    clearError: useCallback(() => setError(null), []),
    setRailState,
    clearRailScope,
    twoFactor,
  };
}
